import { liftPromiseState } from '@confluence/lifted-promise';

import {
	isSuperbatchRecoveryEnabled,
	SUPERBATCH_RECOVERY_TIMEOUT_MS,
} from './superbatchRecovery.ts';

export function onScriptsAndCSSLoaded(
	tags: (HTMLScriptElement | HTMLLinkElement)[],
	signal?: AbortSignal,
): Promise<void> {
	if (isSuperbatchRecoveryEnabled()) {
		return liftPromiseState(
			new Promise<void>((resolve, reject) => {
				if (signal?.aborted) {
					reject(new Error('Superbatch loading timed out'));
					return;
				}
				if (!tags.length) {
					resolve();
					return;
				}
				let remaining = tags.length;
				let timeout: number | undefined;
				const cleanups: (() => void)[] = [];
				const cleanup = () => {
					window.clearTimeout(timeout);
					cleanups.forEach((removeListeners) => removeListeners());
					signal?.removeEventListener('abort', onAbort);
				};
				const onAbort = () => {
					cleanup();
					reject(new Error('Superbatch loading timed out'));
				};
				if (signal) {
					// Share the overall recovery deadline instead of starting another 30 seconds.
					signal.addEventListener('abort', onAbort, { once: true });
				} else {
					timeout = window.setTimeout(() => {
						cleanup();
						reject(new Error('Superbatch resource loading timed out'));
					}, SUPERBATCH_RECOVERY_TIMEOUT_MS);
				}
				tags.forEach((tag) => {
					const onLoad = () => {
						tag.setAttribute('data-loaded', 'true');
						remaining -= 1;
						if (!remaining) {
							cleanup();
							resolve();
						}
					};
					const onError = () => {
						cleanup();
						reject(new Error('Superbatch resource failed to load'));
					};
					tag.addEventListener('load', onLoad, { once: true });
					tag.addEventListener('error', onError, { once: true });
					cleanups.push(() => {
						tag.removeEventListener('load', onLoad);
						tag.removeEventListener('error', onError);
					});
				});
			}),
		);
	}
	return liftPromiseState(
		Promise.all(
			tags.map((script) => {
				return new Promise<void>((resolve) => {
					script.onload = () => {
						resolve();
						script.setAttribute('data-loaded', 'true');
					};
				});
			}),
		).then(() => {}),
	);
}
